import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { Header, VStack } from 'tio-ui/components/layout';
import { eq } from 'tio-ui/utilities';
import { PhPath, PhBuildings, PhCaretDown, PhUserCircle } from 'ember-phosphor-icons';
import MegaMenuNav from 'tio-employee/components/reimagined/mega-menu-nav';
import {
  DEMO_CLIENT_LIST,
  DEMO_SCENARIO_LIST,
  getDemoClientIdFromUrl,
  getDemoScenarioIdFromUrl,
  getDemoData,
  getDemoScenario,
  setDemoClientId,
  setDemoScenarioId,
  type DemoClientId,
  type DemoScenarioId,
} from 'tio-employee/utils/career-pathways-demo-data';

export default class CareerPathwaysLayout extends Component {
  @tracked selectedClientId: DemoClientId = getDemoClientIdFromUrl();
  @tracked selectedScenarioId: DemoScenarioId = getDemoScenarioIdFromUrl();
  @tracked isClientMenuOpen = false;
  @tracked isScenarioMenuOpen = false;

  get clients() {
    return DEMO_CLIENT_LIST;
  }

  get scenarios() {
    return DEMO_SCENARIO_LIST;
  }

  get selectedClient() {
    return getDemoData(this.selectedClientId);
  }

  get selectedScenario() {
    return getDemoScenario(this.selectedScenarioId);
  }

  get selectedClientName() {
    return this.selectedClient.client.name;
  }

  get selectedClientIndustry() {
    return this.selectedClient.client.industry;
  }

  get selectedScenarioName() {
    return this.selectedScenario.name;
  }

  get selectedScenarioDescription() {
    return this.selectedScenario.description;
  }

  selectClient = (clientId: DemoClientId) => {
    setDemoClientId(clientId);
  };

  selectScenario = (scenarioId: DemoScenarioId) => {
    setDemoScenarioId(scenarioId);
  };

  toggleClientMenu = () => {
    this.isClientMenuOpen = !this.isClientMenuOpen;
    this.isScenarioMenuOpen = false;
  };

  toggleScenarioMenu = () => {
    this.isScenarioMenuOpen = !this.isScenarioMenuOpen;
    this.isClientMenuOpen = false;
  };

  closeClientMenu = () => {
    this.isClientMenuOpen = false;
  };

  closeScenarioMenu = () => {
    this.isScenarioMenuOpen = false;
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="min-h-screen overflow-hidden">
      <Header class="border-b border-gray-200 pb-4 shrink-0">
        <div class="max-w-7xl mx-auto w-full px-6">
          <div class="flex items-center justify-between gap-4">
            <div class="flex items-center gap-3">
              <PhPath @size={{32}} class="text-violet-600" />
              <span class="text-2xl font-bold text-gray-900">TIO Reimagined</span>
              <span
                class="ml-2 px-2 py-1 text-xs font-medium bg-violet-100 text-violet-700 rounded-full"
              >DEMO</span>
            </div>

            {{! Client Selector }}
            <div class="flex items-center gap-3">
              <div class="relative">
                <button
                  type="button"
                  class="flex items-center gap-3 px-4 py-2 bg-violet-50 border border-violet-200 rounded-lg hover:bg-violet-100 transition-colors"
                  {{on "click" this.toggleClientMenu}}
                >
                  <PhBuildings @size={{20}} class="text-violet-600" />
                  <div class="text-left">
                    <div class="text-sm font-semibold text-violet-900">
                      {{this.selectedClientName}}
                    </div>
                    <div class="text-xs text-violet-600">{{this.selectedClientIndustry}}</div>
                  </div>
                  <PhCaretDown @size={{16}} class="text-violet-600" />
                </button>

                {{#if this.isClientMenuOpen}}
                  {{! Backdrop }}
                  <button
                    type="button"
                    class="fixed inset-0 z-10"
                    aria-label="Close client menu"
                    {{on "click" this.closeClientMenu}}
                  ></button>

                  {{! Dropdown }}
                  <div
                    class="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 z-20 overflow-hidden"
                  >
                    <div class="px-4 py-2 bg-gray-50 border-b border-gray-200">
                      <span class="text-xs font-semibold text-gray-500 uppercase tracking-wide">Demo
                        Client</span>
                    </div>
                    {{#each this.clients as |client|}}
                      <button
                        type="button"
                        class="w-full flex items-center gap-3 px-4 py-3 hover:bg-violet-50 transition-colors text-left
                          {{if
                            (eq client.id this.selectedClientId)
                            'bg-violet-50 border-l-4 border-violet-600'
                          }}"
                        {{on "click" (fn this.selectClient client.id)}}
                      >
                        <PhBuildings @size={{24}} class="text-gray-400 shrink-0" />
                        <div class="flex-1 min-w-0">
                          <div class="font-semibold text-gray-900">{{client.name}}</div>
                          <div class="text-sm text-gray-500">{{client.industry}}</div>
                          <div class="text-xs text-gray-400 mt-1">
                            {{client.employeeCount}}
                            employees •
                            {{client.taUtilization}}% TA utilization
                          </div>
                        </div>
                        {{#if (eq client.id this.selectedClientId)}}
                          <div class="w-2 h-2 bg-violet-600 rounded-full shrink-0"></div>
                        {{/if}}
                      </button>
                    {{/each}}
                  </div>
                {{/if}}
              </div>

              {{! Scenario Selector }}
              <div class="relative">
                <button
                  type="button"
                  class="flex items-center gap-3 px-4 py-2 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-colors"
                  {{on "click" this.toggleScenarioMenu}}
                >
                  <PhUserCircle @size={{20}} class="text-emerald-600" />
                  <div class="text-left">
                    <div class="text-sm font-semibold text-emerald-900">
                      {{this.selectedScenarioName}}
                    </div>
                    <div class="text-xs text-emerald-600">User Scenario</div>
                  </div>
                  <PhCaretDown @size={{16}} class="text-emerald-600" />
                </button>

                {{#if this.isScenarioMenuOpen}}
                  {{! Backdrop }}
                  <button
                    type="button"
                    class="fixed inset-0 z-10"
                    aria-label="Close scenario menu"
                    {{on "click" this.closeScenarioMenu}}
                  ></button>

                  {{! Dropdown }}
                  <div
                    class="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 z-20 overflow-hidden"
                  >
                    <div class="px-4 py-2 bg-gray-50 border-b border-gray-200">
                      <span class="text-xs font-semibold text-gray-500 uppercase tracking-wide">User
                        Scenario</span>
                    </div>
                    {{#each this.scenarios as |scenario|}}
                      <button
                        type="button"
                        class="w-full flex items-center gap-3 px-4 py-3 hover:bg-emerald-50 transition-colors text-left
                          {{if
                            (eq scenario.id this.selectedScenarioId)
                            'bg-emerald-50 border-l-4 border-emerald-600'
                          }}"
                        {{on "click" (fn this.selectScenario scenario.id)}}
                      >
                        <PhUserCircle @size={{24}} class="text-gray-400 shrink-0" />
                        <div class="flex-1 min-w-0">
                          <div class="font-semibold text-gray-900">{{scenario.name}}</div>
                          <div class="text-sm text-gray-500">{{scenario.description}}</div>
                        </div>
                        {{#if (eq scenario.id this.selectedScenarioId)}}
                          <div class="w-2 h-2 bg-emerald-600 rounded-full shrink-0"></div>
                        {{/if}}
                      </button>
                    {{/each}}
                  </div>
                {{/if}}
              </div>
            </div>
          </div>
        </div>
      </Header>

      {{! Reimagined Navigation - Mega-menu with dropdowns }}
      <MegaMenuNav @scenarioId={{this.selectedScenarioId}} />

      {{! Main content area - constrained width with proper overflow }}
      <div class="flex-1 bg-gray-50 overflow-y-auto overflow-x-hidden min-w-0">
        <div class="max-w-7xl mx-auto w-full px-6 pt-4 pb-6">
          {{outlet}}
        </div>
      </div>
    </VStack>
  </template>
}
